import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryCache, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/toaster';

// Eager: first-paint routes
import Auth from '@/pages/Auth';
import Index from '@/pages/Index';
import NotFound from '@/pages/NotFound';

// Lazy: everything else is fetched when the screen is opened
const Profile = lazy(() => import('@/pages/Profile'));
const EditProfile = lazy(() => import('@/pages/EditProfile'));
const Settings = lazy(() => import('@/pages/Settings'));
const AdminErrors = lazy(() => import('@/pages/AdminErrors'));
const Billing = lazy(() => import('@/pages/Billing'));
const PrivacySettings = lazy(() => import('@/pages/PrivacySettings'));
const ChangePassword = lazy(() => import('@/pages/ChangePassword'));
const NotificationSettings = lazy(() => import('@/pages/NotificationSettings'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const AdminLegalSettings = lazy(() => import('@/pages/AdminLegalSettings'));
import BottomNavigation from '@/components/BottomNavigation';
import ProtectedRoute from '@/components/ProtectedRoute';
import ScrollToTop from '@/components/ScrollToTop';
import NavigationAnalytics from '@/components/NavigationAnalytics';
import SwipeBackGesture from '@/components/SwipeBackGesture';
import MandatoryProfileDialog from '@/components/MandatoryProfileDialog';
import { useAppSplash } from '@/hooks/useAppSplash';
import { usePageTracking } from '@/hooks/usePageTracking';
import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LoginUpgradePrompt } from '@/components/premium/LoginUpgradePrompt';
import { UserPresenceProvider } from '@/contexts/UserPresenceContext';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { AnalyticsProvider } from '@/contexts/AnalyticsContext';
import { MilestoneCelebrationProvider } from '@/contexts/MilestoneCelebrationContext';
import { AchievementCelebrationProvider } from '@/contexts/AchievementCelebrationContext';
import { ROUTES } from '@/constants/routes';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import PWAStatus from '@/components/PWAStatus';
const Matches = lazy(() => import('@/pages/Matches'));
const CreateMatch = lazy(() => import('@/pages/CreateMatch'));
const Messages = lazy(() => import('@/pages/Messages'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const GroupsPage = lazy(() => import('@/pages/GroupsPage'));
const GroupPage = lazy(() => import('@/pages/GroupPage'));
const EditGroup = lazy(() => import('@/pages/EditGroup'));
const GroupHallOfFamePage = lazy(() => import('@/pages/GroupHallOfFame'));
const GroupFeesPage = lazy(() => import('@/pages/GroupFees'));
const GroupAnnouncements = lazy(() => import('@/pages/GroupAnnouncements'));
const YourGroupsPage = lazy(() => import('@/pages/YourGroupsPage'));
const GroupStats = lazy(() => import('@/pages/GroupStats'));
const CreateGroup = lazy(() => import('@/pages/CreateGroup'));
const Tournaments = lazy(() => import('@/pages/Tournaments'));
const CreateTournament = lazy(() => import('@/pages/CreateTournament'));
const TournamentDetails = lazy(() => import('@/pages/TournamentDetails'));
const EditTournament = lazy(() => import('@/pages/EditTournament'));
const MatchDetail = lazy(() => import('@/pages/MatchDetail'));
const ContactUs = lazy(() => import('@/pages/ContactUs'));
const Help = lazy(() => import('@/pages/Help'));
const FeatureGuide = lazy(() => import('@/pages/FeatureGuide'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Feed = lazy(() => import('@/pages/Feed'));
const GlobalRankings = lazy(() => import('@/pages/GlobalRankings'));
const StyleGuide = lazy(() => import('@/pages/StyleGuide'));
const MarketingLanding = lazy(() => import('@/pages/MarketingLanding'));
const PadelRulesGuide = lazy(() => import('@/pages/PadelRulesGuide'));
const PadelRulesGuideEs = lazy(() => import('@/pages/PadelRulesGuideEs'));
const AmericanoTournamentGuideEs = lazy(() => import('@/pages/AmericanoTournamentGuideEs'));
const PadelLeagueGuideEs = lazy(() => import('@/pages/PadelLeagueGuideEs'));
const PadelVsPickleballGuide = lazy(() => import('@/pages/PadelVsPickleballGuide'));
const AmericanoTournamentGuide = lazy(() => import('@/pages/AmericanoTournamentGuide'));
const MexicanoTournamentGuide = lazy(() => import('@/pages/MexicanoTournamentGuide'));
const PadelLeagueGuide = lazy(() => import('@/pages/PadelLeagueGuide'));
const PadelVsTennisGuide = lazy(() => import('@/pages/PadelVsTennisGuide'));
const GetStartedPadelGuide = lazy(() => import('@/pages/GetStartedPadelGuide'));
const PadelCourtsNearMe = lazy(() => import('@/pages/PadelCourtsNearMe'));
const LeaguesPage = lazy(() => import('@/pages/LeaguesPage'));
const LeagueCreate = lazy(() => import('@/pages/LeagueCreate'));
const LeagueDetails = lazy(() => import('@/pages/LeagueDetails'));
const LeagueJoin = lazy(() => import('@/pages/LeagueJoin'));
import { usePendingLeagueInvite } from '@/hooks/usePendingLeagueInvite';

import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { reportError } from '@/lib/errorReporting';
import { BookingFeedbackPrompt } from '@/components/booking/BookingFeedbackPrompt';
import { ContextErrorBoundary } from '@/components/ContextErrorBoundary';
import { NotificationPermissionPrompt } from '@/components/NotificationPermissionPrompt';
import { AnalyticsWrapper } from '@/components/analytics/AnalyticsWrapper';
import { PrivacyConsentBanner } from '@/components/analytics/PrivacyConsentBanner';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import PullToRefresh from '@/components/PullToRefresh';

// Optimized React Query configuration for PWA
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error('🔥 Query error:', error);
      reportError(error, {
        source: 'client_query',
        context: { query_key: JSON.stringify(query.queryKey).slice(0, 300) },
      });
    },
  }),
  defaultOptions: {
    queries: {
      // Use React Query v5 defaults with PWA optimization
      staleTime: 2 * 60 * 1000, // 2 minutes - matches service worker cache
      gcTime: 10 * 60 * 1000, // 10 minutes - shorter for memory efficiency
      // Enable background refetching for real-time feel
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Retry with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on auth errors or 404s
        if (error?.status === 401 || error?.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: 1,
      // Global mutation error handling
      onError: (error) => {
        console.error('🔥 Mutation error:', error);
        reportError(error, { source: 'client_mutation' });
      },
    },
  },
});

function MandatoryFieldsPromptWrapper() {
  const { missingRequiredFields, showMandatoryFieldsPrompt, setShowMandatoryFieldsPrompt } = useAuth();
  
  return (
    <MandatoryProfileDialog
      open={showMandatoryFieldsPrompt}
      onOpenChange={setShowMandatoryFieldsPrompt}
      missingFields={missingRequiredFields}
    />
  );
}

function AppContent() {
  useAppSplash(); // Dismiss the launch splash once the shell is ready
  usePageTracking(); // Automatic page tracking
  useKeyboardNavigation(); // Enable keyboard navigation shortcuts
  useVersionCheck(); // Proactive version checking for auto-updates
  usePendingLeagueInvite(); // Redeem stored league invite after sign-in
  const isMobile = useIsMobile();
  
  
  return (
    <SidebarProvider defaultOpen={false}>
      <SwipeBackGesture enabled={true}>
        <AnalyticsWrapper>
          <div className="flex min-h-screen w-full">
            {/* Sidebar for desktop only */}
            {!isMobile && <AppSidebar />}
            
            <SidebarInset className="flex-1 flex flex-col w-full min-h-screen">
              <PullToRefresh>
               <NavigationAnalytics />
               <LoginUpgradePrompt />
              <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
              <Routes>
                        <Route path="/onboarding" element={<Onboarding />} />
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/cookies" element={<CookiePolicy />} />

                        
                        <Route path="/" element={<MarketingLanding />} />
                        <Route path="/guides/padel-rules" element={<PadelRulesGuide />} />
                        <Route path="/guias/reglas-del-padel" element={<PadelRulesGuideEs />} />
                        <Route path="/guias/torneo-americano-padel" element={<AmericanoTournamentGuideEs />} />
                        <Route path="/guias/como-organizar-una-liga-de-padel" element={<PadelLeagueGuideEs />} />
                        <Route path="/guides/padel-vs-pickleball" element={<PadelVsPickleballGuide />} />
                        <Route path="/guides/americano-padel-tournament" element={<AmericanoTournamentGuide />} />
                        <Route path="/guides/mexicano-padel-tournament" element={<MexicanoTournamentGuide />} />
                        <Route path="/guides/how-to-organize-a-padel-league" element={<PadelLeagueGuide />} />
                        <Route path="/guides/padel-vs-tennis" element={<PadelVsTennisGuide />} />
                        <Route path="/guides/how-to-get-started-playing-padel" element={<GetStartedPadelGuide />} />
                        <Route path="/padel-courts-near-me" element={<PadelCourtsNearMe />} />
                        <Route path="/blog/padel-rules" element={<PadelRulesGuide />} />
                        <Route path={ROUTES.HOME} element={<ProtectedRoute><Index /></ProtectedRoute>} />
                        <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                        <Route path="/user/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                        <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
                        <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
                        
                        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                        <Route path="/admin/errors" element={<ProtectedRoute><AdminErrors /></ProtectedRoute>} />
                        <Route path="/admin/legal" element={<ProtectedRoute><AdminLegalSettings /></ProtectedRoute>} />

                        <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
                        <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
                        <Route path="/privacy-settings" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
                        <Route path="/settings/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
                        <Route path="/settings/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                        
                        <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
                        <Route path="/matches/:matchId" element={<ProtectedRoute><MatchDetail /></ProtectedRoute>} />
                        <Route path="/create-match" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
                        <Route path="/matches/create" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
                        
                        <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
                        <Route path="/groups/create" element={<ProtectedRoute><CreateGroup /></ProtectedRoute>} />
                        <Route path="/your-groups" element={<ProtectedRoute><YourGroupsPage /></ProtectedRoute>} />
                        <Route path="/groups/:groupId" element={<ProtectedRoute><GroupPage /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/edit" element={<ProtectedRoute><EditGroup /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/hall-of-fame" element={<ProtectedRoute><GroupHallOfFamePage /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/fees" element={<ProtectedRoute><GroupFeesPage /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/announcements" element={<ProtectedRoute><GroupAnnouncements /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/stats" element={<ProtectedRoute><GroupStats /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/create-tournament" element={<ProtectedRoute><CreateTournament /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/tournaments/:tournamentId" element={<ProtectedRoute><TournamentDetails /></ProtectedRoute>} />
                        <Route path="/groups/:groupId/tournaments/:tournamentId/edit" element={<ProtectedRoute><EditTournament /></ProtectedRoute>} />
                        
                        <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
                        
                        <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
                        <Route path="/tournaments/create" element={<ProtectedRoute><CreateTournament /></ProtectedRoute>} />
                        <Route path="/tournaments/:tournamentId" element={<ProtectedRoute><TournamentDetails /></ProtectedRoute>} />
                        <Route path="/tournaments/:tournamentId/edit" element={<ProtectedRoute><EditTournament /></ProtectedRoute>} />

                        <Route path="/leagues" element={<ProtectedRoute><LeaguesPage /></ProtectedRoute>} />
                        <Route path="/leagues/new" element={<ProtectedRoute><LeagueCreate /></ProtectedRoute>} />
                        <Route path="/leagues/join/:code" element={<LeagueJoin />} />
                        <Route path="/leagues/:leagueId" element={<ProtectedRoute><LeagueDetails /></ProtectedRoute>} />

                        
                        
                        <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                        <Route path="/messages/group/:groupId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                        <Route path="/messages/user/:userId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                        <Route path="/contact" element={<ContactUs />} />
                         <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
                         <Route path="/what-you-can-do" element={<FeatureGuide />} />

                         <Route path="/rankings" element={<ProtectedRoute><GlobalRankings /></ProtectedRoute>} />
                         <Route path="/style-guide" element={<StyleGuide />} />
                         
                        
                        <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              <Toaster />
              <PWAStatus />
              <PWAInstallPrompt />
              <NotificationPermissionPrompt />
              <PrivacyConsentBanner />
              <BookingFeedbackPrompt />
              <MandatoryFieldsPromptWrapper />
              <BottomNavigation />
              <ScrollToTop />
              </PullToRefresh>
            </SidebarInset>
          </div>
        </AnalyticsWrapper>
      </SwipeBackGesture>
    </SidebarProvider>
  );
}

function App() {
  console.log('🚀 App: Component rendering');

  return (
    <AppErrorBoundary>
      <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
          <BrowserRouter>
            <ContextErrorBoundary contextName="Authentication">
              <AuthProvider>
                <AnalyticsProvider>
                  <UserPresenceProvider>
                    <NavigationProvider>
                      <MilestoneCelebrationProvider>
                        <AchievementCelebrationProvider>
                          <div className="min-h-screen bg-background">
                            <AppContent />
                          </div>
                        </AchievementCelebrationProvider>
                      </MilestoneCelebrationProvider>
                    </NavigationProvider>
                  </UserPresenceProvider>
                </AnalyticsProvider>
              </AuthProvider>
            </ContextErrorBoundary>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
      </HelmetProvider>
    </AppErrorBoundary>
  );
}

export default App;
